import type { Tab } from './index';
import { esc, timeAgo } from '../utils';
import { workerFetch } from '../worker';

interface WireItem {
  title:       string;
  link:        string;
  date:        string;
  description: string;
  source:      string;
  categories:  string[];
}
interface WireResp { items: WireItem[]; count: number; cachedAt: string; }

const SOURCE_COLOR: Record<string, string> = {
  'PR Newswire':   '#a78bfa',
  'SEC 8-K':       '#60a5fa',
  'FDA Approvals': '#fb923c',
};

const CAT_COLOR: Record<string, string> = {
  'FDA':      '#fb923c',
  'Clinical': '#f472b6',
  'M&A':      '#60a5fa',
  'Earnings': 'var(--warn)',
  'Capital':  '#34d399',
  'Distress': 'var(--red)',
  'General':  'var(--text3)',
};

const SOURCES   = ['All', 'PR Newswire', 'SEC 8-K', 'FDA Approvals'];
const CATS      = ['All', 'FDA', 'Clinical', 'M&A', 'Earnings', 'Capital', 'Distress'];

export class WireNewsTab implements Tab {
  readonly id = 'wire-news';
  private items:         WireItem[] = [];
  private activeSource = 'All';
  private activeCat    = 'All';
  private refreshTimer: ReturnType<typeof setInterval> | null = null;

  private el(id: string): HTMLElement { return document.getElementById(id) as HTMLElement; }

  init(): void {
    this.el('wire-refresh-btn').addEventListener('click', () => this.load(true));

    this.el('wire-source-filters').addEventListener('click', (e: MouseEvent) => {
      const btn = (e.target as HTMLElement).closest<HTMLElement>('[data-wire-source]');
      if (!btn) return;
      this.activeSource = btn.dataset.wireSource!;
      this.renderFilters();
      this.renderFeed();
    });

    this.el('wire-cat-filters').addEventListener('click', (e: MouseEvent) => {
      const btn = (e.target as HTMLElement).closest<HTMLElement>('[data-wire-cat]');
      if (!btn) return;
      this.activeCat = btn.dataset.wireCat!;
      this.renderFilters();
      this.renderFeed();
    });
  }

  render(): void {
    this.load(false);
    if (!this.refreshTimer) {
      this.refreshTimer = setInterval(() => this.load(false), 15 * 60 * 1000);
    }
  }

  destroy(): void {
    if (this.refreshTimer) { clearInterval(this.refreshTimer); this.refreshTimer = null; }
  }

  private async load(force: boolean): Promise<void> {
    if (!force && this.items.length) return; // use cached
    this.el('wire-feed').innerHTML =
      `<div style="padding:20px;color:var(--text3);font-family:var(--mono);font-size:12px;">Loading wire feeds…</div>`;
    try {
      const data = await workerFetch<WireResp>('/api/wire-news');
      this.items = data.items ?? [];
      this.el('wire-updated').textContent = `Cached ${timeAgo(data.cachedAt)}`;
      this.el('wire-count').textContent   = String(data.count);
      this.renderFilters();
      this.renderFeed();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      this.el('wire-feed').innerHTML =
        `<div style="padding:20px;color:var(--red);font-family:var(--mono);font-size:12px;">Error: ${esc(msg)}</div>`;
    }
  }

  private renderFilters(): void {
    const btnStyle = (active: boolean, col: string) =>
      `background:${active ? 'rgba(0,212,255,0.12)' : 'transparent'};` +
      `border:1px solid ${active ? 'rgba(0,212,255,0.4)' : 'var(--border)'};` +
      `color:${active ? 'var(--accent)' : col};` +
      `font-family:var(--mono);font-size:10px;padding:3px 9px;cursor:pointer;border-radius:2px;letter-spacing:0.06em;`;

    // Source filter — show count per source
    const srcCounts: Record<string, number> = { All: this.items.length };
    this.items.forEach(i => { srcCounts[i.source] = (srcCounts[i.source] ?? 0) + 1; });

    this.el('wire-source-filters').innerHTML = SOURCES.map(s =>
      `<button data-wire-source="${esc(s)}" style="${btnStyle(this.activeSource === s, SOURCE_COLOR[s] ?? 'var(--text2)')}">
        ${esc(s)} <span style="opacity:0.6;">(${srcCounts[s] ?? 0})</span>
      </button>`
    ).join('');

    // Category filter — count against current source selection
    const visible = this.activeSource === 'All' ? this.items : this.items.filter(i => i.source === this.activeSource);
    const catCounts: Record<string, number> = { All: visible.length };
    visible.forEach(i => i.categories.forEach(c => { catCounts[c] = (catCounts[c] ?? 0) + 1; }));

    this.el('wire-cat-filters').innerHTML = CATS.map(c =>
      `<button data-wire-cat="${esc(c)}" style="${btnStyle(this.activeCat === c, CAT_COLOR[c] ?? 'var(--text3)')}">
        ${esc(c)} <span style="opacity:0.6;">(${catCounts[c] ?? 0})</span>
      </button>`
    ).join('');
  }

  private renderFeed(): void {
    let filtered = this.activeSource === 'All' ? this.items : this.items.filter(i => i.source === this.activeSource);
    if (this.activeCat !== 'All') filtered = filtered.filter(i => i.categories.includes(this.activeCat));

    if (!filtered.length) {
      this.el('wire-feed').innerHTML =
        `<div class="empty-state">No items for this filter.</div>`;
      return;
    }

    this.el('wire-feed').innerHTML = filtered.map(item => {
      const srcCol  = SOURCE_COLOR[item.source] ?? 'var(--text3)';
      const cats    = item.categories.map(c =>
        `<span style="color:${CAT_COLOR[c] ?? 'var(--text3)'};background:rgba(255,255,255,0.04);border:1px solid rgba(255,255,255,0.08);font-size:9px;padding:1px 5px;border-radius:2px;font-family:var(--mono);letter-spacing:0.06em;">${esc(c)}</span>`
      ).join(' ');
      const ts = item.date ? timeAgo(item.date) : '';

      return `
        <a href="${esc(item.link)}" target="_blank" rel="noopener"
          style="display:block;padding:11px 14px;background:var(--panel);border:1px solid var(--border);
                 border-left:3px solid ${srcCol};border-radius:3px;margin-bottom:5px;
                 text-decoration:none;color:inherit;">
          <div style="display:flex;align-items:flex-start;gap:8px;margin-bottom:5px;">
            <span style="flex-shrink:0;font-size:10px;font-family:var(--mono);color:${srcCol};font-weight:700;letter-spacing:0.04em;min-width:100px;">${esc(item.source)}</span>
            <span style="font-size:13px;color:var(--text);line-height:1.4;flex:1;">${esc(item.title)}</span>
          </div>
          <div style="display:flex;align-items:center;gap:6px;flex-wrap:wrap;">
            ${cats}
            ${ts ? `<span style="font-size:10px;color:var(--text3);font-family:var(--mono);margin-left:auto;">${esc(ts)}</span>` : ''}
          </div>
          ${item.description ? `<div style="font-size:11px;color:var(--text3);margin-top:5px;line-height:1.4;">${esc(item.description)}</div>` : ''}
        </a>`;
    }).join('');
  }
}
